<template>
  <div class="safe-week-summary">
    <p v-if="loading" class="grey-text sc-state-text">Loading safe check summary…</p>

    <p v-else-if="error" class="red-text sc-state-text">
      {{ error }}
    </p>

    <template v-else>
      <div class="sc-header-bar">
        <div class="sc-header-copy">
          <div class="sc-title-row">
            <div class="sc-title">Last 14 Days</div>

            <div class="sc-badge">
              {{ displayRows.length }}
              {{ displayRows.length === 1 ? "submission" : "submissions" }}
            </div>
          </div>

          <div class="sc-subtitle">
            {{ rangeLabel }}
          </div>
        </div>
      </div>

      <p v-if="displayRows.length === 0" class="grey-text sc-state-text">
        No safe checks for the last 14 days.
      </p>

      <template v-else>
        <div class="sc-table-wrap">
          <table class="highlight sc-table">
            <thead>
              <tr>
                <th>Day</th>
                <th>Date</th>
                <th class="right-align">Opening (£)</th>
                <th class="right-align">Closing (£)</th>
                <th class="right-align">Var (£)</th>
                <th class="right-align">Completed By</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="r in displayRows" :key="r._rowKey">
                <td class="sc-cell-day">
                  {{ r.day }}
                </td>

                <td>
                  {{ formatDate(r.trading_date) }}
                </td>

                <td class="right-align">
                  {{ money(r.opening_balance) }}
                </td>

                <td class="right-align">
                  {{ money(r.closing_balance) }}
                </td>

                <td class="right-align" :class="varianceClass(r.variance)">
                  <strong>
                    {{ money(r.variance) }}
                  </strong>
                </td>

                <td class="right-align sc-completed-by">
                  {{ r.completed_by || "—" }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="sc-mobile-list">
          <div v-for="r in displayRows" :key="r._rowKey" class="sc-mobile-card">
            <div class="sc-mobile-top">
              <div>
                <div class="sc-mobile-day">
                  {{ r.day }}
                </div>

                <div class="sc-mobile-date">
                  {{ formatDate(r.trading_date) }}
                </div>
              </div>

              <div class="sc-mobile-var" :class="varianceClass(r.variance)">
                {{ money(r.variance) }}
              </div>
            </div>

            <div class="sc-mobile-grid">
              <div class="sc-mobile-item">
                <span class="sc-mobile-label"> Opening </span>

                <span class="sc-mobile-value">
                  {{ money(r.opening_balance) }}
                </span>
              </div>

              <div class="sc-mobile-item">
                <span class="sc-mobile-label"> Closing </span>

                <span class="sc-mobile-value">
                  {{ money(r.closing_balance) }}
                </span>
              </div>
            </div>

            <div class="sc-mobile-completed">
              <span class="sc-mobile-label"> Completed By </span>

              <span class="sc-mobile-completed-value">
                {{ r.completed_by || "—" }}
              </span>
            </div>
          </div>
        </div>

        <div class="sc-total-row">
          <div class="sc-total-label">Total variance</div>

          <div class="sc-total-value" :class="varianceClass(totalVariance)">
            {{ money(totalVariance) }}
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";

import { safeCheckListWeek } from "@/scripts/sqlApiHelper/safeApi";

const props = defineProps({
  venueId: {
    type: String,
    required: true,
  },

  weekEndingDate: {
    type: String,
    required: true,
  },
});

const loading = ref(false);
const error = ref(null);
const rows = ref([]);
const fromDate = ref("");
const toDate = ref("");

function clean(v) {
  return String(v ?? "").trim();
}

function num(v) {
  const n = Number(v);

  return Number.isFinite(n) ? n : 0;
}

function sortableDate(iso) {
  const v = clean(iso);

  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : "";
}

function sortableTimestamp(value) {
  if (!value) {
    return 0;
  }

  if (typeof value?.toMillis === "function") {
    return value.toMillis();
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  const parsed = Date.parse(String(value));

  return Number.isFinite(parsed) ? parsed : 0;
}

const displayRows = computed(() => {
  const list = Array.isArray(rows.value) ? rows.value : [];

  return list
    .map((r, index) => {
      const tradingDate = clean(r?.trading_date);

      const submittedAt = r?.submitted_at ?? r?.created_at ?? r?.updated_at ?? "";

      const submittedSort = sortableTimestamp(submittedAt);

      const tradingSort = sortableDate(tradingDate);

      const rowId =
        clean(r?.id) ||
        clean(r?.doc_id) ||
        clean(r?._id) ||
        (submittedSort ? `submitted-${submittedSort}` : "") ||
        `${tradingDate || "no-date"}-${index}`;

      return {
        ...r,
        _submittedSort: submittedSort,
        _tradingSort: tradingSort,
        _rowKey: rowId,
      };
    })
    .sort((a, b) => {
      if (a._tradingSort !== b._tradingSort) {
        return b._tradingSort.localeCompare(a._tradingSort);
      }

      if (a._submittedSort !== b._submittedSort) {
        return b._submittedSort - a._submittedSort;
      }

      return String(a._rowKey).localeCompare(String(b._rowKey));
    });
});

const totalVariance = computed(() =>
  displayRows.value.reduce((s, r) => s + num(r.variance), 0)
);

const rangeLabel = computed(() => {
  if (!fromDate.value || !toDate.value) {
    return "Rolling 14-day safe check view";
  }

  return `${formatDate(fromDate.value)} to ${formatDate(toDate.value)}`;
});

function money(v) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(num(v));
}

function formatDate(d) {
  const [y, m, dd] = String(d || "")
    .split("-")
    .map(Number);

  if (!y || !m || !dd) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
  }).format(new Date(y, m - 1, dd));
}

function varianceClass(v) {
  const n = num(v);

  if (n > 0) {
    return "green-text text-darken-2";
  }

  if (n < 0) {
    return "red-text text-darken-2";
  }

  return "grey-text text-darken-1";
}

async function load() {
  const venueId = clean(props.venueId);

  const weekEndingDate = clean(props.weekEndingDate);

  if (!venueId || !weekEndingDate) {
    rows.value = [];
    fromDate.value = "";
    toDate.value = "";

    return;
  }

  loading.value = true;
  error.value = null;

  try {
    const res = await safeCheckListWeek({
      venue_id: venueId,
      week_ending_date: weekEndingDate,
    });

    rows.value = Array.isArray(res?.rows) ? res.rows : [];

    fromDate.value = clean(res?.from_date);

    toDate.value = clean(res?.to_date);
  } catch (e) {
    error.value = e?.message || String(e);

    rows.value = [];
    fromDate.value = "";
    toDate.value = "";
  } finally {
    loading.value = false;
  }
}

function refresh() {
  return load();
}

defineExpose({
  refresh,
});

watch(
  () => [props.venueId, props.weekEndingDate],
  () => load(),
  {
    immediate: true,
  }
);
</script>

<style scoped>
.safe-week-summary {
  width: 100%;
  padding: 16px 18px;
}

.sc-header-bar {
  margin-bottom: 14px;
}

.sc-header-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sc-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.sc-title {
  font-size: 17px;
  font-weight: 700;
  color: #263238;
}

.sc-subtitle {
  font-size: 13px;
  color: #78909c;
}

.sc-badge {
  padding: 5px 10px;
  border-radius: 999px;
  background: #eceff1;
  color: #546e7a;
  font-size: 11px;
  font-weight: 700;
}

.sc-table-wrap {
  border: 1px solid #eceff1;
  border-radius: 10px;
  background: #fff;
  margin-top: 10px;
}

.sc-table {
  margin: 0;
  background: #fff;
  min-width: 720px;
}

.sc-table thead th {
  background: #fafbfc;
  color: #37474f;
  font-weight: 700;
  font-size: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #eceff1;
}

.sc-table tbody td {
  padding: 14px 16px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 14px;
  color: #263238;
}

.sc-table tbody tr:last-child td {
  border-bottom: none;
}

.sc-cell-day {
  font-weight: 700;
}

.sc-completed-by {
  font-weight: 600;
  color: #455a64;
}

.sc-total-row {
  margin-top: 14px;
  padding: 14px 16px;
  border: 1px solid #eceff1;
  border-radius: 10px;
  background: #fafbfc;

  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sc-total-label {
  font-size: 14px;
  font-weight: 700;
}

.sc-total-value {
  font-size: 17px;
  font-weight: 800;
}

.sc-mobile-list {
  display: none;
}

@media (max-width: 600px) {
  .safe-week-summary {
    padding: 12px;
  }

  .sc-table-wrap {
    display: none;
  }

  .sc-mobile-list {
    display: block;
  }

  .sc-mobile-card {
    border: 1px solid #eceff1;
    border-radius: 10px;
    padding: 14px;
    margin-bottom: 10px;
    background: #fff;
  }

  .sc-mobile-top {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 10px;
  }

  .sc-mobile-day {
    font-size: 15px;
    font-weight: 700;
  }

  .sc-mobile-date {
    font-size: 12px;
    color: #78909c;
  }

  .sc-mobile-var {
    font-size: 15px;
    font-weight: 800;
    text-align: right;
    overflow-wrap: anywhere;
  }

  .sc-mobile-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 6px;
  }

  .sc-mobile-item {
    background: #fafbfc;
    border: 1px solid #eef2f4;
    border-radius: 8px;
    padding: 10px;
  }

  .sc-mobile-label {
    font-size: 10px;
    color: #78909c;
  }

  .sc-mobile-value {
    display: block;
    margin-top: 3px;
    font-size: 13px;
    font-weight: 700;
    overflow-wrap: anywhere;
  }

  .sc-mobile-completed {
    margin-top: 10px;
    background: #fafbfc;
    border: 1px solid #eef2f4;
    border-radius: 8px;
    padding: 10px;
  }

  .sc-mobile-completed-value {
    display: block;
    margin-top: 3px;
    font-weight: 700;
    overflow-wrap: anywhere;
  }

  .sc-total-row {
    margin-top: 10px;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .sc-title-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .sc-badge {
    align-self: flex-start;
  }
}
</style>
